// src/_services/customer.service.ts
import { dbRead, dbSet, dbUpdate, dbRemove, saveLog } from './firebase'
import type { CustomerReg, CustomerStatus, LoanData, InstallmentItem } from '../_types'
import { formatCurrency } from '../_utils'
import { DB_CUST_A, DB_DATA_A, DB_CUST_B, DB_DATA_B, DB_CUST_C, DB_DATA_C, DB_CUST_D, DB_DATA_D, DB_CUST_E, DB_DATA_E, DB_PHOTO_BY_ID, DB_REGISTER_FORM, DB_LOAN_BY_ID, DB_STATUS_BY_ID, DB_PAYIN_BY_ID, DB_PAYOUT_BY_ID, DB_BAD_DEBT_BY_ID, DB_REG_CUS_BY_ID } from '../_config/db_paths'

// ─── Paths ────────────────────────────────────────────────────
export const PATH = {
  regCustomer:     DB_CUST_A,
  regAll:          DB_DATA_A,
  loanCustomer:    DB_CUST_B,
  loanAll:         DB_DATA_B,
  activeCustomer:  DB_CUST_C,
  activeAll:       DB_DATA_C,
  closedCustomer:  DB_CUST_D,
  closedAll:       DB_DATA_D,
  badDebtCustomer: DB_CUST_E,
  badDebtAll:      DB_DATA_E,

  registerCustomer: DB_REG_CUS_BY_ID,
  photo:   DB_PHOTO_BY_ID,
  regForm: DB_REGISTER_FORM,

  loan:    DB_LOAN_BY_ID,
  status:  DB_STATUS_BY_ID,
  payin:   DB_PAYIN_BY_ID,
  payout:  DB_PAYOUT_BY_ID,
  badDebt: DB_BAD_DEBT_BY_ID,
}

// ─── Read all customers from a path ──────────────────────────
export async function getAllCustomers(pathKey: 'reg' | 'loan' | 'active' | 'closed' | 'badDebt'): Promise<CustomerReg[]> {
  const pathMap = {
    reg:     PATH.regAll,
    loan:    PATH.loanAll,
    active:  PATH.activeAll,
    closed:  PATH.closedAll,
    badDebt: PATH.badDebtAll,
  }
  const data = await dbRead<Record<string, CustomerReg>>(pathMap[pathKey])
  if (!data) return []
  return Object.values(data)
}

// ─── Save new customer (data_a) ───────────────────────────────
export async function saveNewCustomer(
  uid: string,
  customerId: string,
  reg: CustomerReg
): Promise<void> {
  await dbSet(PATH.regCustomer(customerId), reg)
  await saveLog(uid, 'create', customerId, `สร้างลูกค้าใหม่: ${reg.personal.fullName}`)
}

// ─── Update customer ──────────────────────────────────────────
export async function updateCustomer(
  uid: string,
  customerId: string,
  pathKey: 'reg' | 'loan' | 'active',
  patch: Partial<CustomerReg>
): Promise<void> {
  const pathMap = { reg: PATH.regCustomer, loan: PATH.loanCustomer, active: PATH.activeCustomer }
  await dbUpdate(pathMap[pathKey](customerId), { ...patch, 'meta/updatedAt': Date.now() })
  await saveLog(uid, 'update', customerId)
}

// ─── Move customer: data_a → data_b (approve) ────────────────
export async function approveCustomer(
  uid: string,
  customerId: string,
  reg: CustomerReg,
  loan: LoanData
): Promise<void> {
  const updated: CustomerReg = {
    ...reg,
    meta: { ...reg.meta, status: 'waiting', updatedAt: Date.now() },
  }
  await dbSet(PATH.loanCustomer(customerId), updated)
  await dbSet(PATH.loan(customerId), loan)
  await dbUpdate(PATH.status(customerId), {
    now: { value: 'waiting', lastUpdatedBy: uid, updatedAt: Date.now() },
    [`history/${Date.now()}`]: { uid, value: 'waiting', updatedAt: Date.now() },
  })
  await dbRemove(PATH.regCustomer(customerId))
  await saveLog(uid, 'approve', customerId, `อนุมัติสินเชื่อ ${formatCurrency(loan.actualAmount)} กีบ`)
}

// ─── Move customer: data_b → data_c (payout) and data_b → DB_REG_CUS ─────────────────
export async function payoutCustomer(
  uid: string,
  customerId: string,
  reg: CustomerReg,
  loan: LoanData,
  installments: InstallmentItem[]
): Promise<void> {
  const updated: CustomerReg = {
    ...reg,
    meta: { ...reg.meta, status: 'paid', updatedAt: Date.now() },
  }
  await saveLog(uid, 'create', customerId, `บันทึกข้อมูลการสมัครของลูกค้า ไปยัง DB_REG_CUS: ${reg.personal.fullName}`)
  await dbSet(PATH.registerCustomer(customerId), updated)
  await dbSet(PATH.activeCustomer(customerId), updated)
  await dbSet(PATH.loan(customerId), { ...loan, updatedAt: Date.now(), updatedBy: uid })

  // บันทึกงวดทั้งหมด
  const payinMap: Record<string, InstallmentItem> = {}
  installments.forEach((item) => { payinMap[`${item.seq}`] = item })
  await dbSet(PATH.payin(customerId), payinMap)

  // บันทึก payout (ยอดที่โอนให้ลูกค้า)
  await dbSet(PATH.payout(customerId), {
    amount: loan.payoutAmount,
    createdAt: Date.now(),
    uid,
    updatedAt: Date.now(),
  })

  await dbUpdate(PATH.status(customerId), {
    now: { value: 'paid', lastUpdatedBy: uid, updatedAt: Date.now() },
    [`history/${Date.now()}`]: { uid, value: 'paid', updatedAt: Date.now() },
  })
  await dbRemove(PATH.loanCustomer(customerId))
  await saveLog(uid, 'payout', customerId, `จ่ายเงิน ${formatCurrency(loan.payoutAmount)} กีบ`)
}

// ─── Move customer: data_c → data_d (close) ──────────────────
export async function closeCustomer(uid: string, customerId: string, reg: CustomerReg): Promise<void> {
  const updated = { ...reg, meta: { ...reg.meta, status: 'closed' as CustomerStatus, updatedAt: Date.now() } }
  await dbSet(PATH.closedCustomer(customerId), updated)
  await dbUpdate(PATH.status(customerId), {
    now: { value: 'closed', lastUpdatedBy: uid, updatedAt: Date.now() },
    [`history/${Date.now()}`]: { uid, value: 'closed', updatedAt: Date.now() },
  })
  await dbRemove(PATH.activeCustomer(customerId))
  await saveLog(uid, 'close_account', customerId)
}

// ─── Move customer: data_c → data_e (bad debt) ───────────────
export async function markBadDebt(uid: string, customerId: string, reg: CustomerReg, reason: string): Promise<void> {
  const updated = { ...reg, meta: { ...reg.meta, status: 'stopped' as CustomerStatus, updatedAt: Date.now() } }
  await dbSet(PATH.badDebtCustomer(customerId), updated)
  await dbSet(PATH.badDebt(customerId), { reason, uid, updatedAt: Date.now() })
  await dbUpdate(PATH.status(customerId), {
    now: { value: 'stopped', lastUpdatedBy: uid, updatedAt: Date.now() },
    [`history/${Date.now()}`]: { uid, value: 'stopped', action: 'badDebt', updatedAt: Date.now() },
  })
  await dbRemove(PATH.activeCustomer(customerId))
  await saveLog(uid, 'bad_debt', customerId, reason)
}

// ─── Move customer: data_b → data_d (cancel/reject) ──────────
export async function cancelLoanCustomer(
  uid: string,
  customerId: string,
  reg: CustomerReg,
  reason: string
): Promise<void> {
  const updated: CustomerReg = {
    ...reg,
    meta: { ...reg.meta, status: 'closed' as CustomerStatus, updatedAt: Date.now() },
  }
  await dbSet(PATH.closedCustomer(customerId), updated)
  await dbUpdate(PATH.status(customerId), {
    now: { value: 'closed', lastUpdatedBy: uid, updatedAt: Date.now() },
    [`history/${Date.now()}`]: { uid, value: 'closed', action: 'cancel', reason, updatedAt: Date.now() },
  })
  await dbRemove(PATH.loanCustomer(customerId))
  await saveLog(uid, 'close_account', customerId, `ยกเลิก/ปฏิเสธสินเชื่อ: ${reason}`)
}